'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { apiGet, apiPost } from '@/lib/api'
import { saveStoredAccount } from '@/lib/utils'
import type { Account, AccountProfile } from '@/types'

type AccountProfileState = {
  accountProfile: AccountProfile | null
  profileRefreshing: boolean
  refreshProfile: (currentAccount?: Account | null) => Promise<void>
  reset: () => void
}

export function useAccountProfile(
  account: Account | null,
  setAccount: (next: Account | null) => void,
): AccountProfileState {
  const [accountProfile, setAccountProfile] = useState<AccountProfile | null>(null)
  const [profileRefreshing, setProfileRefreshing] = useState(false)
  // Mirrors the selected account so async responses for a previously selected
  // account are dropped instead of overwriting the new account's profile.
  const activeWxidRef = useRef(account?.wxid || '')

  useEffect(() => {
    activeWxidRef.current = account?.wxid || ''
    if (!account) {
      setAccountProfile(null)
      return
    }
    let cancelled = false
    void apiGet<{
      success: boolean
      profile: AccountProfile | null
    }>('/bot/profile?wxid=' + encodeURIComponent(account.wxid))
      .then((res) => {
        if (!cancelled) setAccountProfile(res.profile)
      })
      .catch(() => {
        if (!cancelled) setAccountProfile(null)
      })
    return () => {
      cancelled = true
    }
  }, [account])

  const refreshProfile = useCallback(
    async (currentAccount: Account | null = account) => {
      if (!currentAccount) return
      setProfileRefreshing(true)
      try {
        const response = await apiPost<{
          success: boolean
          profile: AccountProfile | null
        }>('/bot/profile', { wxid: currentAccount.wxid })
        if (activeWxidRef.current !== currentAccount.wxid) return
        setAccountProfile(response.profile)
        if (response.profile) {
          const nextAccount = {
            ...currentAccount,
            nickName: response.profile.nickName || currentAccount.nickName,
            alias: response.profile.alias || currentAccount.alias,
            avatar: response.profile.avatar || currentAccount.avatar,
          }
          setAccount(nextAccount)
          saveStoredAccount(nextAccount)
        }
      } finally {
        setProfileRefreshing(false)
      }
    },
    [account, setAccount],
  )

  const reset = useCallback(() => {
    setAccountProfile(null)
  }, [])

  return { accountProfile, profileRefreshing, refreshProfile, reset }
}
